import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { SubscriptionStatusResponse } from "../contracts/SubscriptionStatusResponse";
import { SubscriptionPlansResponse } from "../contracts/SubscriptionPlansResponse";
import { CheckoutResponse } from "../contracts/CheckoutResponse";
import { PortalSessionResponse } from "../contracts/PortalSessionResponse";
import { CreateCheckoutRequest } from "../contracts/CreateCheckoutRequest";
import { SubscriptionPlan } from "../contracts/SubscriptionPlan";
import { SubscriptionTier } from "../contracts/SubscriptionTier";
import { SubscriptionStatus } from "../models/SubscriptionStatus";
import { mapResponseToSubscriptionStatus } from "../mappers/SubscriptionStatusMapper";

export const fetchSubscriptionStatus = createAsyncThunk<
  SubscriptionStatus,
  void,
  { rejectValue: ApiErrorResponse }
>("subscription/fetchStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SubscriptionStatusResponse>(
      "/subscription/status"
    );
    return mapResponseToSubscriptionStatus(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load subscription status"));
  }
});

/**
 * Opens the provider customer portal (cancel, payment method, invoices) and
 * resolves to its URL. The caller opens the URL in a new tab.
 */
export const openBillingPortal = createAsyncThunk<
  string,
  void,
  { rejectValue: ApiErrorResponse }
>("subscription/portal", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PortalSessionResponse>(
      "/subscription/portal"
    );
    return response.data.url;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to open billing portal"));
  }
});

/**
 * Confirms a just-completed checkout by its transaction id. The backend reads the
 * transaction back from Paddle and activates the account immediately, so the page
 * reflects the new subscription without waiting for the webhook.
 */
export const syncCheckout = createAsyncThunk<
  SubscriptionStatus,
  string,
  { rejectValue: ApiErrorResponse }
>("subscription/sync", async (transactionId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<SubscriptionStatusResponse>(
      "/subscription/sync",
      { transactionId }
    );
    return mapResponseToSubscriptionStatus(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to confirm checkout"));
  }
});

export const fetchSubscriptionPlans = createAsyncThunk<
  SubscriptionPlansResponse,
  void,
  { rejectValue: ApiErrorResponse }
>("subscription/fetchPlans", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SubscriptionPlansResponse>(
      "/subscription/plans"
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load subscription plans"));
  }
});

/**
 * Resolves the chosen plan into the provider price + customer for the account and
 * returns them. The caller opens the Paddle.js checkout overlay with the price.
 */
export const startSubscriptionCheckout = createAsyncThunk<
  CheckoutResponse,
  { plan: SubscriptionPlan; tier: SubscriptionTier },
  { rejectValue: ApiErrorResponse }
>("subscription/startCheckout", async ({ plan, tier }, { rejectWithValue }) => {
  try {
    const request: CreateCheckoutRequest = { plan, tier };
    const response = await axiosInstance.post<CheckoutResponse>(
      "/subscription/checkout",
      request
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to start checkout"));
  }
});
